/**
 * Layout principal pós-login: sidebar fixa + topbar + outlet.
 * Em mobile a sidebar vira drawer.
 * Em desktop a sidebar pode ser recolhida (preferência salva em localStorage).
 */

import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import clsx from "clsx";

import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { ErrorBoundary } from "./ErrorBoundary";

const SIDEBAR_COLLAPSED_KEY = "envpulse:sidebar-collapsed";

export function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  // Sidebar desktop colapsada — persistido em localStorage.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  const location = useLocation();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // localStorage indisponível — ignora
    }
  }, [collapsed]);

  return (
    /*
     * Insets laterais cobrem o modo paisagem, onde o entalhe fica na borda
     * esquerda ou direita e comeria a sidebar / o conteúdo.
     */
    <div className="flex h-full bg-surface-50 pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] dark:bg-slate-950">
      {/* Sidebar desktop (>= md) — sempre tema escuro (parte da identidade) */}
      <aside
        className={clsx(
          "hidden md:flex md:flex-col md:bg-[#0a0f1a] transition-[width] duration-200 ease-out",
          "pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]",
          collapsed ? "md:w-16" : "md:w-64",
        )}
      >
        <Sidebar
          onNavigate={() => setMobileOpen(false)}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((c) => !c)}
        />
      </aside>

      {/* Sidebar mobile (drawer) — sempre expandida */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-[#0a0f1a] pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] animate-slide-in-right md:hidden">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </>
      )}

      {/* Conteúdo */}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          left={
            <button
              type="button"
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100 md:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          }
        />
        <main
          /* pb extra: o indicador de home do iPhone cobre o rodapé do scroll. */
          className="flex-1 overflow-y-auto p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:p-6 md:pb-[calc(1.5rem+env(safe-area-inset-bottom))] lg:p-8 lg:pb-[calc(2rem+env(safe-area-inset-bottom))]"
        >
          {/* key={pathname} remonta o conteúdo → dispara animação a cada nav */}
          <div key={location.pathname} className="animate-fade-in-up">
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  );
}
